import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'

import { getDefaultLocale, getLocaleCodeFromCountryLang } from '@src/i18n/locale.util'
import type { CountryCodeTBB, LangTBB, LocaleCode } from '@src/i18n/locales'

export type Dictionary = typeof EN_US

export const getDictionary = (_country: string, _lang: string) => {
  const defaultLocale = getDefaultLocale()
  const country = _country ?? defaultLocale.countryCodeLowercase
  const lang = _lang ?? defaultLocale.code
  const localeCode = getLocaleCodeFromCountryLang(country, lang)
  const dictionaries: Record<LocaleCode, Dictionary> = {
    'en-US': EN_US,
    'en-CA': EN_CA,
    'fr-CA': FR_CA,
    'en-GB': EN_GB,
    'en-AU': EN_AU,
    'fr-FR': FR_FR,
  }
  const dictionary = dictionaries[localeCode as LocaleCode]
  return dictionary ?? dictionaries[defaultLocale.code]
}

/**
 * Bundled fallback copy for the video liability waiver. The localized copy is
 * normally served by the legal API; this is shown only when that request fails.
 */
const VIDEO_WAIVER_BODY_EN = `<p style="text-align: center; font-weight: bold; font-size: larger;">WARNING</p>
  <p style="font-weight: bold;">We want to keep you safe! Please consult your physician, assess your fitness level, and follow all safety instructions before beginning any exercise program, nutrition plan, and/or using any equipment.</p>
  <p>Beachbody provides a variety of exercise programs, some of which are physically demanding and high-intensity in nature. For this reason, you must listen to your body, use common sense, take breaks, and hydrate as needed to avoid injury. If at any time you feel any discomfort, pain, dizziness, light-headedness, shortness of breath, or nausea, stop exercising immediately and consult your physician. Incorrect or excessive training can result in serious injury or death.</p>
  <p>When doing any outdoor workouts or activities, be aware of your surroundings and avoid anything that could hurt you or disrupt your exercise, especially when walking or running in public areas such as sidewalks and streets.</p>
  <p>If you have any unique or special medical conditions, such as if you're pregnant, have diabetes or asthma, or if you have a history of knee, ankle, hip, shoulder or spinal (back or neck) problems, you must consult your physician to understand all potential risks and complications of using our exercise programs, nutrition plans, and/or equipment, and receive approval from them to proceed before beginning. Failure to do so could result in significant injury to you and others (including, if applicable, your unborn child). <span style="font-weight:bold;">By engaging in any exercise program, nutrition plan, and/or using any equipment,
  you assume all dangers, hazards and risks of injury or death.</span></p>
  <p style="text-align: center; font-weight: bold; text-decoration: underline; margin-bottom: 0; font-size: larger;">SAFE USE OF EQUIPMENT</p>
  <p style="text-align: center; font-weight: bold; text-decoration: underline; margin-top: 0;">FITNESS EQUIPMENT PRESENTS HAZARDS WHICH, IF NOT AVOIDED, COULD CAUSE SERIOUS INJURY OR DEATH</p>
  <p>Certain programs may utilize equipment, such as the Myx fitness bike, jump ropes, resistance bands, loops, pull up bars, benches, stability balls, sliding discs, weights and other equipment which, if not used correctly, could lead to serious injury or death. Carefully review all safety, care and use instructions and manuals prior to beginning.</p>
  <p style="margin-bottom: 0;">For your safety, you must:</p>
  <ul style="margin-top: 0;">
    <li>Use any equipment shown in the workouts <span style="text-decoration:underline;">only</span> as demonstrated, using proper form.</li>
    <li>Inspect any equipment for wear or damage prior to each use and refrain from using any equipment that appears damaged, worn or defective.</li>
    <li>Keep children, pets and any other obstacles away from equipment and exercise area at all times.</li>
    <li>Always use a secure, proper, and stable anchor for any equipment that requires hanging or attaching.</li>
    <li>Always exercise caution during use of any equipment to make sure you do not lose your grip or control, such as making sure your hands are not wet or sweaty.</li>
    <li>Ensure exercise bands are firmly secure and assembled to prevent slipping, snapping, recoiling and injury to yourself or anyone else.</li>
    <li>Ensure your workout bench and pull up bar is stable, strong enough to hold your weight and does not move during use. If you do not think you can safely perform exercises with your bench or pull up bar, or you do not have the proper equipment, you should do the modifier exercises instead.</li>
  </ul>
  <p style="text-align: center; font-weight: bold; text-decoration: underline; font-size: larger;">SAFE USE OF THE MYX FITNESS BIKE</p>
  <p style="margin-bottom: 0;">In addition to the warnings above, for your safety when using the Myx fitness bike, tablet and related accessories ("bike"), you must:</p>
  <ul style="margin-top: 0;">
    <li>Carefully review and follow the Product Manual and instructional video tutorials before beginning to ensure proper and safe set-up, use and operation of the bike.</li>
    <li>Use the bike only after securely locking the seat and handlebars into place.</li>
    <li>Keep children, hands, pets, loose clothing, shoelaces, jewelry and anything else away from any moving parts.</li>
    <li>Use caution when mounting and dismounting.</li>
    <li>Not use, store or operate the bike outdoors or in damp or wet locations.</li>
    <li>Operate the bike on a solid level surface.</li>
    <li>Adhere to all weight limitations &#8211; user weight must not exceed 350 pounds (158 kg).</li>
    <li>Adhere to all height restrictions &#8211; users must be between 4'11" and 6'8" tall (1.5 m and 2.03 m tall).</li>
    <li>Adhere to any age restrictions, as stated in the Product Manual.</li>
    <li>Adhere to the minimum clearance required around the bike, as stated in the Product Manual.</li>
    <li>Perform Myx bike workouts <span style="text-decoration: underline;">only with</span> the Myx bike (and never with any street, road or any other type of bike).</li>
  </ul>
  <p>Please consult your physician before engaging in heart rate training, especially if you are pregnant, are younger than 18 years old, have any underlying heart, lung, or any other medical condition, or are unsure about elevating your heart rate to the max. If you are younger than 18 years old, please also get your parent or guardian's permission before beginning. Heart rate, calorie, cadence and other metrics and values displayed on the bike during use may be inaccurate and must be used and interpreted for reference only.</p>
  <p>To the fullest extent permitted by law, Beachbody, LLC and its parent, affiliate, and subsidiary companies will not be liable to any person or entity for any injury, death, damage or loss caused or alleged to be caused directly or indirectly by any exercise programs, workouts, nutritional supplements or plans, equipment (including without limitation the Myx fitness bike), advice or any other products, services or materials.</p>`

const VIDEO_WAIVER_BODY_FR = `<p style="text-align: center; font-weight: bold; font-size: larger;">AVERTISSEMENT</p>
  <p style="font-weight: bold;">Nous tenons à votre sécurité</p>
  <p style="font-weight: bold;">Veuillez consulter votre médecin, évaluer votre niveau de condition physique et respecter toutes les consignes de sécurité avant de commencer tout programme d'entraînement ou de nutrition et/ou d'utiliser tout équipement.</p>
  <p>Beachbody propose une large gamme de programmes d'entraînement, dont certains sont physiquement exigeants et de haute intensité. C'est pourquoi vous devez impérativement écouter votre corps, faire preuve de bon sens, vous hydrater correctement et faire des pauses afin d'éviter toute blessure. Si vous ressentez inconfort, douleurs, étourdissements, vertiges, essoufflement ou nausées, arrêtez immédiatement tout exercice et consultez votre médecin. Une mauvaise pratique ou un excès d'exercice physique peuvent entraîner des blessures graves ou la mort.</p>
  <p>Lors d'activité sportives en plein air, veillez à votre environnement et évitez ce qui pourrait vous blesser ou interrompre votre entraînement, particulièrement en cas de marche ou course à pied dans des lieux publics, tels que les rues ou sur des trottoirs.</p>
  <p>Si vous souffrez de problèmes médicaux particuliers ou spéciaux, si vous êtes une femme enceinte, que vous avez du diabète ou de l'asthme, ou des antécédents de problèmes au niveau des genoux, des chevilles, des épaules ou de la colonne vertébrale (dos ou cou), vous devez consulter votre médecin pour comprendre tous les risques et complications potentiels liés à l'utilisation de nos programmes d'entraînement ou de nutrition et/ou de nos équipements, et obtenir son autorisation avant de commencer. Négliger ce conseil pourrait entraîner des blessures graves pour vous et les autres (y compris, le cas échéant, votre enfant à naître). <span style="font-weight:bold;">En vous engageant dans un programme d'entraînement ou de nutrition et/ou en utilisant quelque équipement que ce soit, vous en assumez tous les risques et dangers, y compris les risques de blessure ou de mort.</span></p>
  <p style="text-align: center; font-weight: bold; text-decoration: underline; margin-bottom: 0; font-size: larger;">UTILISATION SÛRE DE L'ÉQUIPEMENT</p>
  <p style="text-align: center; font-weight: bold; text-decoration: underline; margin-top: 0;">LES ÉQUIPEMENTS DE FITNESS PRÉSENTENT DES RISQUES, QUI, EN CAS DE PROBLÈME, SONT SUSCEPTIBLES D'ENTRAÎNER DES BLESSURES GRAVES OU LA MORT</p>
  <p>Certains programmes peuvent impliquer l'utilisation d'équipements tels que le vélo de fitness Myx, des cordes à sauter, bandes élastiques, barres de traction, bancs, ballons de stabilité, disques coulissants, poids et autres équipements qui, s'ils ne sont pas utilisés correctement, sont susceptibles de provoquer des blessures graves ou la mort. Avant de commencer, lisez attentivement l'ensemble des manuels et instructions de sécurité, d'entretien et d'utilisation.</p>
  <p style="margin-bottom: 0;">Pour votre sécurité, vous devez :</p>
  <ul style="margin-top: 0;">
   <li>Utiliser les équipements montrés dans les entraînements <span style="text-decoration:underline;">uniquement</span> comme indiqué dans les démonstrations et de manière appropriée.</li>
   <li>Inspecter les équipements avant chaque utilisation et vous abstenir d'utiliser un équipement s'il semble endommagé, usé ou défectueux.</li>
   <li>Tenir les enfants, animaux et tout obstacle à distance de vos équipements et de votre zone d'entraînement à tout moment.</li>
   <li>Utiliser un ancrage sécurisé, approprié et stable pour tout équipement nécessitant d'être suspendu ou fixé.</li>
   <li>Utiliser tout équipement avec prudence de façon à ne jamais perdre prise et à le contrôler en permanence, par exemple en vous assurant que vos mains ne sont ni mouillées, ni en sueur.</li>
   <li>Vous assurer que les bandes élastiques sont fermement sécurisées et assemblées afin d'éviter qu'elles ne glissent, s'échappent, dérapent ou ne vous blessent, vous ou votre entourage.</li>
   <li>Vous assurer que votre banc et votre barre de traction sont stables et assez solides pour supporter votre poids et ne pas bouger lors de l'utilisation. Si vous ne pensez pas être en mesure d'effectuer certains exercices en toute sécurité avec votre banc ou votre barre de traction, ou si vous ne disposez pas de l'équipement adéquat, vous devez opter pour les versions modifiées des exercices.</li>
  </ul>
  <p style="text-align: center; font-weight: bold; text-decoration: underline; font-size: larger;">UTILISATION SÛRE DU VÉLO DE FITNESS MYX</p>
  <p style="margin-bottom: 0;">En plus des avertissements ci-dessus, pour votre sécurité lors de l'utilisation du vélo de fitness Myx, de la tablette et des accessoires connexes (le « vélo »), vous devez :</p>
  <ul style="margin-top: 0;">
   <li>Lire et suivre attentivement le manuel du produit et les vidéos d'instructions avant de commencer, afin de vous assurer de préparer, utiliser et manipuler le vélo correctement et en toute sécurité.</li>
   <li>N'utiliser le vélo qu'après avoir fixé la selle et le guidon de façon sécurisée.</li>
   <li>Tenir les enfants, animaux, mains, vêtements amples, lacets, bijoux et autres éloignés des éléments en mouvement.</li>
   <li>Faire preuve de prudence lorsque vous montez sur le vélo ou en descendez.</li>
   <li>Ne pas stocker, utiliser ou manipuler le vélo en extérieur ou dans des lieux humides ou mouillés.</li>
   <li>Manipuler le vélo sur une surface stable et plane.</li>
   <li>Respecter les limitations de poids : le poids de l'utilisateur ne doit pas excéder 158 kg (350 livres).</li>
   <li>Respecter les restrictions de taille : l'utilisateur doit mesurer entre 1,50 m et 2,03 m (4'11" et 6'8").</li>
   <li>Respecter les restrictions d'âge stipulées dans le manuel du produit.</li>
   <li>Respecter l'espace minimal requis autour du vélo stipulé dans le manuel du produit.</li>
   <li>Suivre les entraînements prévus pour le vélo Myx <span style="text-decoration: underline;">uniquement</span> avec le vélo Myx (et en aucun cas avec un vélo de ville, VTT ou tout autre type de vélo).</li>
  </ul>
  <p>Veuillez consulter votre médecin avant de commencer des entraînements cardio, en particulier si vous êtes enceinte, âgé(e) de moins de 18 ans, que vous avez des problèmes cardiaques, pulmonaires ou toute autre problème médical, ou que vous doutez des conséquences d'une augmentation maximale de votre rythme cardiaque. Si vous avez moins de 18 ans, veuillez également demander au préalable la permission à vos parents ou tuteurs. Le rythme cardiaque, les calories, la cadence et les autres données et valeurs affichées sur le vélo lors de son utilisation pourraient s'avérer inexactes et ne doivent être utilisées qu'à titre de référence.</p>
  <p>Dans toutes les mesures permises par la loi, Beachbody, LLC et ses sociétés mères, affiliées et filiales ne seront tenues responsables envers aucune personne ou entité d'aucun décès, blessure, dommage ou perte causés ou présumés causés directement ou indirectement par tout programme ou séance d'entraînement, complément alimentaire, programme de nutrition, équipement (y compris, sans toutefois s'y limiter, le vélo de fitness Myx), conseil ou tout autre produit, service ou matériel.</p>`

export const EN_US = {
  'checkout-form-success': {
    'partner.success': 'This confirms {{partner}} is my BODi Partner',
    'partner.disclaimer':
      'Only your contact and product information will be made available to your free BODi Partner who may reach out to help support you with your health and wellness goals. You can stop sharing your contact information at any time.',
  },
  'set-password-form': {
    title: 'Access your BODi App Profile',
    cta: 'SET PASSWORD',
    ruleMinEightChars: 'At least 8 characters',
    ruleOneUpper: 'Upper case letters (A-Z)',
    ruleOneLower: 'Lower case letters (a-z)',
    ruleOneNumber: 'Numbers (0-9)',
    checklist: JSON.stringify([
      'Access content in the BODi app',
      'Manage your account details',
      'View previous orders',
      'Manage memberships and subscriptions',
    ]),
    'confirmation-title': 'All set! Get the app.',
    'confirmation-title-ios': 'Launch your fitness journey',
    'confirmation-subtitle': 'Enjoy BODi on your phone, tablet, bike or computer!',
    'confirmation-subtitle-ios': 'Access BODi on your phone, tablet, TV, or computer',
    'confirmation-cta': 'GET STARTED',
  },
  'login-form': {
    title: 'Login',
    email: 'Email',
    password: 'Password',
    cta: 'Login',
    'forgot-password': 'Forgot Password',
    'email-required': 'Email address is required',
    'email-invalid': 'Please enter a valid email address',
    'password-required': 'Password is required',
    'invalid-credentials': 'The email or password you entered is incorrect. Please try again.',
    'unknown-error': `Oops… we've got an error.`,
  },
  'order-confirmation': {
    yourOrderIsConfirmed: 'Your order is confirmed',
    orderNumber: 'Order number: {{orderNumber}}',
    youWillGetConfirmationAt: 'You’ll get an order confirmation soon at: {{customerEmail}}',
    printReceipt: 'Print receipt',
    yourOrder: 'Your Order',
    subtotal: 'Subtotal',
    estimatedTax: 'Estimated tax',
    total: 'Total',
    billingAndPayment: 'Billing and Payment',
    cardDetails: `{{cardBrand}} ending in {{cardNumber}}, expires {{cardExpiration}}`,
    'total-savings': 'Total Savings',
    shipping: 'Shipping and Handling',
    'error-unknown-title': 'Oops… try that again',
    'error-unknown-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-unknown-cta': 'Try again',
  },
  test: {
    title: 'Home of the Commerce Web',
  },
  common: {
    currency: '${{amount}}',
    saving: 'Saving',
    suggestions: 'Suggestions',
    and: 'and',
    'see-all': 'See all',
    'bodi.com': 'bodi.com',
    'date-pattern': 'MM/DD/YYYY',
    'partner-placeholder': 'Partner ID or Email (optional)',
    quantity: 'Qty: {{quantity}}',
    'error-unknown-title': 'Oops… we’ve got an error',
    'error-unknown-message': 'We were unable to add this to your cart.',
    'error-unknown-cta': 'Okay',
    'error-retry-title': 'Oops… try that again',
    'error-retry-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-retry-cta': 'Try again',
    'identity-outage-title': 'Unable to Create Account',
    'identity-outage-cta': 'Homepage',
    'identity-outage-message': 'We were unable to create your account. Please try again later. ',
    'page-title-suffix': '| BODi by Beachbody',
    'app-title-suffix': '| BODi',
    'view-cart': 'View Cart',
    description: 'Description',
    'how-to-use': 'How to Use',
    'key-ingredients': 'Key Ingredients',
    'error-quantity': 'You cannot add more than {{quantity}} of this item to your cart.',
    share: 'Share',
    free: 'Free',
    select: 'Select',
    login: 'Login',
    'login-join': 'Login/Join',
    'my-account': 'My Account',
    'my-dashboard': 'My Dashboard',
    'digital-purchases': 'Digital Purchases',
    favorites: 'Favorites',
    badges: 'Badges',
    'no-badges-available': 'No badges available.',
    tracking: 'Tracking',
    'my-progress': 'My Progress',
    with: 'with',
    'try-free': 'Try for FREE!',
    upNext: 'Up Next',
    'view-program': 'View Program',
    'retake-quiz': 'Retake Quiz',
  },
  carousel: {
    'next-slide': 'Next Slide',
    'previous-slide': 'Previous Slide',
    'slide-number': 'Slide {{number}}',
  },
  'content-details': {
    optional: '* Optional',
    programOverview: 'Program Overview',
    overview: 'Overview',
    equipmentNeeded: 'Equipment needed',
    programFocus: 'Program focus',
    meet: 'MEET',
    startWorkout: 'Start Workout',
    watchVideo: 'Watch video',
    commit: 'Commit',
    committed: 'In Progress',
    startProgram: 'Start Program',
    leave: 'Leave',
    reset: 'Reset',
    enroll: 'Enroll',
    enrolled: 'Enrolled',
    favorite: 'Favorite',
    favorited: 'Favorited',
    before: 'Before',
    after: 'After',
    openPdf: 'Open PDF',
    openLink: 'Open Link',
    backToTop: 'Back to top',
    badgeProgress: 'Badge Progress',
    badgeProgressCount: '{{completed}} of {{total}}',
    playVideo: 'Play video',
    trailer: 'Trailer',
    unenrollTitle: 'Are you sure you want to leave this program?',
    unenrollConfirm: 'Leave',
    unenrollCancel: 'Stay',
    resetProgressTitle: 'Want to restart this program?',
    resetProgressOptionResetTitle: 'Yes, restart {{contentTitle}}',
    resetProgressOptionResetDescription:
      'All progress will be cleared, and you’ll start this program from Day 1.',
    resetProgressOptionKeepTitle: 'No, keep my progress',
    resetProgressOptionKeepDescription:
      'The video watch history won’t reset. You’ll keep making progress toward your badge.',
    resetProgressProceed: 'Proceed',
    enrollLimitTitle: 'Commitment takes focus',
    enrollLimitDescription:
      'You’re already enrolled in 3 programs. Leave one of your current programs to commit to this one.',
    enrollLimitConfirm: 'Got it',
    trackSelectionTitle: 'Choose your track',
    trackSelectionDescription:
      'Pick the track that fits your goals. You can switch tracks anytime by leaving this one first.',
    trackSelectionConfirm: 'Commit',
    trackSelectionCalendarLabel: 'View calendar',
    enrollmentConfirmationTitle: 'You’re committed!',
    enrollmentConfirmationDescription:
      '{{trackTitle}} is ready for you. Your schedule is waiting in Workouts.',
    enrollmentConfirmationCta: 'Let’s go',
    restDay: 'Rest Day',
    optionalWorkout: 'Optional',
    dayLabel: 'Day {{number}}',
  },
  postWorkout: {
    greatJobToday: 'Great job today, {{name}}!',
    doneForToday: 'Done for today',
    thatsHowItsDone: 'That’s how it’s done!',
  },
  'product-page': {
    shakeology: 'Shakeology',
    'facet-title-flavor': 'Flavor',
    'facet-title-packaging': 'Packaging',
    'facet-title-subscriptionFrequency': 'Frequency',
    'facet-title-keyIngredient': 'Key Ingredient',
    'facet-title-size': 'Size',
    quantity: 'Quantity',
    'add-to-cart': 'Add to Cart',
    'subscription-discount-disclaimer':
      '^Sale price for initial purchase only, subsequent renewals are {{amount}}',
    'pinch-to-zoom': 'Pinch to zoom',
  },
  'geo-redirection-modal': {
    title: 'Change shipping country?',
    subtitle:
      "It seems you're in a different region. Would you like to update your shipping country?",
    'select-your-country': 'Select Your Country',
    confirm: 'Confirm',
    cancel: 'No Thanks',
  },
  programsPage: {
    title: 'Programs',
    metaTitle: 'BODi Fitness Programs',
    description:
      'Find the BODi workout program that’s right for you. With 140+ programs, designed to get you results in just a couple weeks, you’ll find workouts you love to do.',
    socialFeedTitle: 'Follow the journey',
    socialFeedSubtitle: 'See what the community is sharing',
    learnMore: 'Learn More',
    goToProgram: 'Go to Program',
  },
  workoutsPage: {
    title: 'Workouts',
    metaTitle: 'BODi Workouts',
    description:
      'Find the workouts you’ll love to do. From weightlifting to yoga, cardio to barre, BODi has workouts for every fitness level and time commitment.',
  },
  allWorkoutsPage: {
    title: 'Workouts',
    metaTitle: 'Browse all BODi Workouts',
    description:
      "Find a workout from BODi's collection of expert trainer-led sessions. Search based on duration, skill level, equipment needed, and more.",
    viewResults: 'View workouts',
    loadingMore: 'Loading more workouts',
  },
  allFitnessProgramsPage: {
    title: 'Programs',
    metaTitle: 'Browse all BODi Fitness Programs',
    description:
      'Find the BODi workout program that’s right for you. Filter by trainer, duration, fitness level, equipment, and more.',
    viewResults: 'View programs',
    loadingMore: 'Loading more programs',
  },
  badgesPage: {
    title: 'Badges',
    metaTitle: 'BODi Badges',
    description: 'View your earned badges and track your progress.',
  },
  fitnessQuizPage: {
    metaTitle: 'Unlock free BODi workouts with our Fitness Quiz',
    titleSuffix: 'BODi',
    description:
      'Take a 1-minute fitness quiz to unlock your free, personalized workouts from BODi (formerly Beachbody).',
    keywords: 'Fitness quiz',
  },
  programQuiz: {
    back: 'Back',
    skip: 'Skip',
    skipAlertTitle: 'Don’t miss out!',
    skipAlertMessage: 'Answer at least one question for personalized results',
    skipAlertCta: 'Got It',
    continue: 'Continue',
    processingTitle: 'Hang Tight!',
    processingMessage: 'Finding your matches…',
    errorTitle: 'We couldn’t load your results',
    errorMessage: 'Please close the quiz and try again.',
    close: 'Close',
    greeting: 'Hey {{firstName}},',
    greetingMessage: 'here are your results…',
    topRecommendation: 'Your top recommendation',
    otherRecommendations: 'Your other recommendations',
    getStarted: 'Get started',
    viewProgram: 'View Program',
    findYourPerfectProgram: 'Find your perfect program',
    takeTheQuiz: 'Take the quiz',
  },
  favoritesPage: {
    workouts: 'Workouts',
    programs: 'Programs',
    blocks: 'Blocks',
    recipes: 'Recipes',
    lessons: 'Lessons',
    more: 'More',
    recentlySaved: 'Recently Saved',
    sortAZ: 'A–Z',
    emptyAllHeading: 'Favorites',
    emptyAllDescription: 'Select the heart on anything you love to save it here.',
    browsePrograms: 'Browse Programs',
    emptyTab: 'No favorites in this category.',
  },
  homePage: {
    welcome: 'Welcome back!',
    welcomeWithName: 'Welcome, {{firstName}}!',
    goTo: 'Go to {{title}}',
    myProgress: 'My Progress',
    limitedEditionBadges: 'Current Limited Edition Badges',
    workoutsCompleted: 'Workouts Completed',
    thisWeek: 'This week',
    lastWeek: 'Last week',
    recentlyEarned: 'Recently Earned',
    badgeProgress: 'Badge Progress',
    extraSubtitle: "Let's get started!",
    whatsNewBlog: "What's New On The Blog",
  },
  postLogin: {
    successHeadline: 'You’ve been signed in successfully.',
    continue: 'Continue',
    dontShowAgain: 'Don’t show me this again',
  },
  accountPage: {
    account: 'Account',
    accountSubheader: 'Manage and update your BODi account info',
    accountSettings: 'Account Settings',
    shippingAddresses: 'Shipping Addresses',
    orders: 'Orders',
    subscriptions: 'Subscriptions & Memberships',
    name: 'Name',
    email: 'Email',
    emailChangeHint: 'To change your email address, please contact us',
    emailChangeFaqUrl: 'https://faq.bodi.com/us/en/contactus-support?category_id=1757',
    password: 'Password',
    language: 'Language',
    pricacyAndPreferences: 'Privacy and Preferences',
    updateYourPrivacyPreferences: 'Update your privacy and email preferences',
    edit: 'Edit',
    editName: 'Edit Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    editPassword: 'Edit Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    editLanguage: 'Edit Language',
    update: 'Update',
    cancel: 'Cancel',
    nameUpdated: 'Your name has been updated',
    passwordUpdated: 'Your password has been updated',
    languageUpdated: 'Your language has been updated',
    unsupported: '(unsupported)',
    error: 'Unable to save changes, please try again later.',
  },
  accountPayments: {
    noPaymentMethods:
      'Currently, you don’t have any saved payment information. Click the “Add” button to save a payment method.',
    primary: 'Primary',
    expired: 'Expired',
    add: 'Add',
    edit: 'Edit',
    update: 'Update',
    delete: 'Delete Card',
    deleteModalTitle: 'Delete payment method?',
    deleteModalDescription:
      'Are you sure you want to delete this payment method? This action cannot be undone.',
    deleteModalCancel: 'Cancel',
    deleteModalDelete: 'Delete',
    addPaymentMethod: 'Add Payment Method',
    editPaymentMethod: 'Edit Payment Method',
    billTo: 'Bill to',
    sameAsShippingAddress: 'Same as primary shipping address',
    expires: 'Expires',
    expiresShort: 'Exp.',
    firstName: 'First Name',
    lastName: 'Last Name',
    country: 'Country',
    address: 'Address',
    addressNumber: 'Apartment, suite, etc. (optional)',
    city: 'City',
    state: JSON.stringify({
      US: 'State',
      CA: 'Province',
    }),
    zip: JSON.stringify({
      US: 'Zip',
      CA: 'Postal Code',
    }),
    setAsPrimaryPayment: 'Set as primary payment method',
    save: 'Save',
    cancel: 'Cancel',
    paymentMethodCreated: 'Payment method created',
    paymentMethodUpdated: 'Payment method updated',
    paymentMethodDeleted: 'Payment method deleted',
    error: 'Unable to save changes, please try again later.',
    updateWarning:
      '<b>Important:</b> Changes to the Payment Methods will not update any existing orders which you have placed. To update subscriptions, please go to the <a class="underline" href="{{subscriptionsAndMembershipsUrl}}">Memberships & Subscriptions</a> tab.',
  },
  'cart-page': {
    'your-cart': 'Your cart',
    'cart-empty': 'Your cart is currently empty',
    'cart-empty-cta': 'Continue Shopping',
    'continue-to-checkout': 'Continue to Checkout',
    'order-summary': 'Order Summary',
    subtotal: 'Subtotal',
    'subtotal-disclaimer': '*Shipping & taxes calculated at checkout',
  },
  'forgot-password': {
    email: 'Email',
    'reset-password': 'Reset your password',
    'enter-email':
      'Enter the email address you used to sign up and we will send you an email with how to set or reset your password',
    'send-email': 'Send Email',
    'email-sent': 'Email Sent',
    'send-instructions':
      'If an account with {{email}} exists, we\u2019ll send reset instructions to that address.',
    'help-center':
      'Didn\u2019t get an email? Please check your <a href="https://faq.bodi.com/us/en/answer?Id=1273" target="_blank">help center</a>.',
  },
  'create-password': {
    title: 'Create your password',
    description:
      'Enter the email address you used for your order. We\u2019ll send you an email called \u201cYour BODi Password Reset.\u201d Because this is your first time signing in, use the link in that email to create your password.',
  },
  'update-password': {
    'page-title': 'Update Password',
    'page-heading': 'Change your password',
    'page-description':
      'Your password helps protect your account. Choose a new one that’s strong and unique, using the requirements below.',
    submit: 'Change Password',
    cancel: 'Return to My Account',
    'password-updated': 'Your password has been updated',
    'success-message': 'We have updated your password. Time to get back in the game.',
    'continue-to-account': 'Return to My Account',
  },
  'outage-splash': {
    heading: 'System under maintenance',
    description: 'Please excuse us, but this page is temporarily offline. Please try again later.',
  },
  countries: {
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    FR: 'France',
  } satisfies Record<CountryCodeTBB, string>,
  languages: {
    en: 'English',
    fr: 'French',
    es: 'Spanish',
  } satisfies Record<LangTBB, string>,
  'media-panel': {
    'go-to-program': 'Go to Program',
    'start-workout': 'Start Workout',
    'watch-video': 'Watch Video',
    'coming-soon': 'COMING SOON',
    'associated-content': 'Related Content',
    'works-well-for': 'Works well for',
    'equipment-needed': 'Equipment needed',
    optional: '* Optional',
    'container-equivalents': 'Container Equivalents',
    'vegan-container-equivalents': 'Vegan Container equivalents',
    'view-pdf': 'View PDF',
    'copy-link': 'Copy link',
    'copy-link-success': 'Link Copied!',
    favorite: 'Favorite',
    favorited: 'Favorited',
    share: 'Share',
  },
  'content-share-modal': {
    'join-me': 'Join me for {{title}}, a 3-week fitness program: ',
    share: 'Share',
    email: 'Email',
    'copy-link': 'Copy Link',
    'copy-link-success': 'Copied!',
  },
  faq: {
    'page-title': 'BODi Support',
    'page-description':
      'Contact customer support and read frequently asked questions about BODi subscriptions, Shakeology, and more.',
    'hero-title': 'How can we help you?',
    'hero-subtitle': 'Search for answers or browse topics below',
    'search-placeholder': 'Search for help...',
    'self-service-title': 'Self-Service Tools',
    'browse-categories-title': 'Browse FAQs by Topic',
    'top-answers-title': 'Top Answers',
    'see-more': 'See more',
    'see-less': 'See less',
    'see-more-results': 'See all answers',
    'all-answers-title': 'All Answers',
    'all-answers-description': 'Browse all available answers from our support knowledge base.',
    'search-results-title': 'Search Results',
    'search-results-for': 'Results for',
    'search-results-count': '{count} results found',
    'search-no-results': 'No results found',
    'search-no-results-message':
      "We couldn't find any answers matching your search. Try different keywords or browse our topics below.",
    'search-try-again': 'Try searching again',
    'search-back-to-home': 'Back to FAQ Home',
    'not-found-title': 'Oops...',
    'not-found-message':
      "Sorry, we couldn't find what you're looking for. It may have been moved or removed.",
    'sms-title': 'Text with BODi',
    'sms-text-with-bodi': 'Text with BODi',
    'sms-text-with-bodi-description': 'Start a conversation with our support team via text message',
    'sms-login-to-text': 'Log In to Text with BODi',
    'sms-login-description':
      'Sign in to start a conversation with our support team via text message',
    'sms-mobile-number': 'Phone Number',
    'sms-text-us': 'Text Us',
    'sms-hours-of-operation': 'Hours of Operation',
    'sms-sent-successfully': 'SMS sent successfully!',
    'sms-failed': 'Failed to send SMS. Please try again.',
    'sms-phone-required': 'Phone number is required',
    'sms-phone-invalid': 'Please enter a valid US phone number',
    'sms-error-occurred': 'An error occurred. Please try again.',
    'sms-greeting': 'Hi {{userName}}, just verify or change your number to get started.',
    'sms-phone-placeholder': '+1 (555) 555-5555',
    'sms-description':
      'Click Text Us to send and receive customer support messages with us. Usage and data rates may apply.',
    'sms-current-time-label': 'It is currently {{currentTime}}',
    'sms-outside-hours': 'SMS service is currently unavailable outside business hours.',
    'sms-confirmation-title': 'Your Request Has Been Submitted',
    'sms-confirmation-subtitle':
      'Thank you for starting a text conversation. Please check your device for our response.',
    email: 'Email BODi',
    'email-description': 'Send us an email and we will get back to you as soon as possible.',
    'busy-mode-close-button': 'Got it, thanks.',
  },
  'user-registration-form-errors': {
    'firstName.required': 'Please enter a first name.',
    'firstName.invalid': 'Maximum allowed length is 30 characters.',
    'lastName.required': 'Please enter a last name.',
    'lastName.invalid': 'Maximum allowed length is 30 characters.',
    'email.exists':
      '<span>You already own this digital product, <a href="/login-bodweb?locale=en_US">login</a> to view your account.<span>',
    'email.invalid': 'Please enter a valid email address.',
    'password.invalid': 'Invalid Password',
    'password.reused': 'The new password must be different than the last password.',
    'password.mustMatch': 'Your passwords must match!',
    'password.incorrect': 'Your old password was not entered correctly.',
    disclaimer:
      'By checking the box, you acknowledge you have read, understand, and agree to the BODi <link1>Terms and Conditions<link1> and <link2>Privacy Policy<link2>.',
    tncErrorMessage: 'Please accept the terms and conditions to continue.',
  },
  'user-registration-form': {
    email: 'Email',
    password: 'Password',
    firstName: 'First Name',
    lastName: 'Last Name',
    login: 'Log In',
    haveAnAccount: 'Have an account?',
    accountCreated: 'Account created successfully!',
    congrats: 'Congrats!',
    accountCreated2: 'Your account has been created.',
    continueToLogin: 'Continue to Login',
  },
  badges: {
    inProgress: 'In Progress',
    earned: 'Earned',
    available: 'Available',
    limitedEdition: 'Limited edition',
  },
  'badge-detail': {
    share: 'Share',
    'go-to-program': 'Go to Program',
    'save-image': 'Save Image',
    back: 'Back',
    'remaining-workouts': 'Remaining Workouts',
    'next-badge': 'Next Badge',
    'share-load-error': 'Unable to load badge share details. Please try again.',
  },
  'primary-badge-modal': {
    title: 'Ready for round {{iteration}}?',
    subtitle: 'Commit now to re-earn your badge.',
    close: 'Close',
  },
  search: {
    title: 'Search',
    placeholder: 'Search BODi',
    'recent-searches': 'Recent searches',
    'clear-all': 'Clear All',
    'no-results': 'No results found for',
    'no-results-found': 'No results found',
    'close-label': 'Close search',
    'results-for': 'Search results for "{{query}}"',
    'results-count': '{count} results',
    results: 'results',
    'items-count': '({{count}} items)',
    filters: 'Filters',
    'clear-filters': 'Clear filters',
    'view-results': 'View results',
    sortBy: 'Sort by:',
    viewResults: 'View results',
    loadingMore: 'Loading more',
    noResultsTitle: "You've stretched us too thin.",
    noResultsDescription: 'Try to loosen up your search and start seeing results instantly.',
    errorTitle: 'Looks like we pulled a muscle',
    errorDescription: 'Please try again. If the issue persists we will resolve it soon',
    retry: 'Retry',
    category: 'Category',
    trainer: 'Trainer / Expert',
    workoutDuration: 'Duration',
    programDuration: 'Program Duration',
    level: 'Fitness Level',
    fitnessLevelBeginner: 'Beginner',
    fitnessLevelIntermediate: 'Intermediate',
    fitnessLevelAdvanced: 'Advanced',
    fitnessLevelAllLevels: 'All Levels',
    muscleGroup: 'Muscle Group',
    bodyPart: 'Body Part',
    equipment: 'Equipment',
    program: 'Program',
    container: 'Container',
    dish: 'Dish',
    mainIngredient: 'Main Ingredient',
    meal: 'Meal',
    worksWellFor: 'Works Well For',
    type: 'Type',
    workoutType: 'Workout Type',
    programType: 'Program Type',
    noEquipment: 'No Equipment',
    noEquipmentFacetValue: 'No Equipment',
    studioWorkoutsFacetValue: 'Studio Workouts',
    studioWorkouts: 'Studio Workouts',
    sortRecommended: 'Most Popular',
    sortNewest: 'Newest',
    sortTitleAsc: 'A-Z',
    numberWeeks: 'Number of Weeks',
    'tab-all': 'All',
    'tab-programs': 'Programs',
    'tab-workouts': 'Workouts',
    'tab-recipes': 'Recipes',
    'tab-lessons': 'Lessons',
    'tab-programMaterials': 'Program Materials',
    'tab-other': 'Other',
  },
  'video-waiver': {
    body: VIDEO_WAIVER_BODY_EN,
    'checkbox-label':
      ' By checking the box and clicking "Accept", I acknowledge I have read, understand, and agree to this warning and waiver.',
    accept: 'accept',
    decline: 'decline',
  },
}

export const EN_CA: Dictionary = {
  'user-registration-form-errors': {
    ...EN_US['user-registration-form-errors'],
    'email.exists':
      '<span>You already own this digital product, <a href="/login-bodweb?locale=en_CA">login</a> to view your account.<span>',
  },
  'user-registration-form': EN_US['user-registration-form'],
  'checkout-form-success': {
    'partner.success': 'This confirms {{partner}} is my BODi partner',
    'partner.disclaimer':
      'Only your contact and product information will be made available to your free BODi Partner who may reach out to help support you with your health and wellness goals. You can stop sharing your contact information at any time.',
  },
  'login-form': EN_US['login-form'],
  'order-confirmation': {
    yourOrderIsConfirmed: 'Your order is confirmed',
    orderNumber: 'Order number: {{orderNumber}}',
    youWillGetConfirmationAt: 'You’ll get an order confirmation soon at: {{customerEmail}}',
    printReceipt: 'Print receipt',
    yourOrder: 'Your Order',
    subtotal: 'Subtotal',
    estimatedTax: 'Estimated tax',
    total: 'Total',
    billingAndPayment: 'Billing and Payment',
    cardDetails: `{{cardBrand}} ending in {{cardNumber}}, expires {{cardExpiration}}`,
    'total-savings': 'Total Savings',
    shipping: 'Shipping and Handling',
    'error-unknown-title': 'Oops… try that again',
    'error-unknown-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-unknown-cta': 'Try again',
  },
  test: {
    title: 'Home of the Commerce Web',
  },
  common: {
    currency: '${{amount}}',
    saving: 'Saving',
    suggestions: 'Suggestions',
    and: 'and',
    'see-all': 'See all',
    'bodi.com': 'bodi.com',
    'date-pattern': 'MM/DD/YYYY',
    'partner-placeholder': 'Partner ID or Email (optional)',
    quantity: 'Qty: {{quantity}}',
    'error-unknown-title': 'Oops… we’ve got an error',
    'error-unknown-message': 'We were unable to add this to your cart.',
    'error-unknown-cta': 'Okay',
    'error-retry-title': 'Oops… try that again',
    'error-retry-message': 'Something went wrong behind the scenes. Try that move again.',
    'error-retry-cta': 'Try again',
    'identity-outage-title': 'Unable to Create Account',
    'identity-outage-message': 'We were unable to create your account. Please try again later. ',
    'identity-outage-cta': 'Homepage',
    'page-title-suffix': '| BODi by Beachbody',
    'app-title-suffix': '| BODi',
    'view-cart': 'View Cart',
    share: 'Share',
    description: 'Description',
    'how-to-use': 'How to Use',
    'key-ingredients': 'Key Ingredients',
    'error-quantity': 'You cannot add more than {{quantity}} of this item to your cart.',
    free: 'Free',
    select: 'Select',
    login: 'Login',
    'login-join': 'Login/Join',
    'my-account': 'My Account',
    'my-dashboard': 'My Dashboard',
    'digital-purchases': 'Digital Purchases',
    favorites: 'Favorites',
    badges: 'Badges',
    'no-badges-available': 'No badges available.',
    tracking: 'Tracking',
    'my-progress': 'My Progress',
    with: 'with',
    'try-free': 'Try for FREE!',
    upNext: 'Up Next',
    'view-program': 'View Program',
    'retake-quiz': 'Retake Quiz',
  },
  postWorkout: EN_US.postWorkout,
  'product-page': {
    shakeology: 'Shakeology',
    'facet-title-flavor': 'Flavour',
    'facet-title-packaging': 'Packaging',
    'facet-title-subscriptionFrequency': 'Frequency',
    'facet-title-keyIngredient': 'Key Ingredient',
    'facet-title-size': 'Size',
    quantity: 'Quantity',
    'add-to-cart': 'Add to Cart',
    'subscription-discount-disclaimer':
      '^Sale price for initial purchase only, subsequent renewals are {{amount}}',
    'pinch-to-zoom': 'Pinch to zoom',
  },
  carousel: EN_US.carousel,
  'content-details': {
    optional: '* Optional',
    programOverview: 'Program Overview',
    overview: 'Overview',
    equipmentNeeded: 'Equipment Needed',
    programFocus: 'Program focus',
    meet: 'MEET',
    startWorkout: 'Start Workout',
    watchVideo: 'Watch video',
    commit: 'Commit',
    committed: 'In Progress',
    startProgram: 'Start Program',
    leave: 'Leave',
    reset: 'Reset',
    enroll: 'Enroll',
    enrolled: 'Enrolled',
    favorite: 'Favorite',
    favorited: 'Favorited',
    before: 'Before',
    after: 'After',
    openPdf: 'Open PDF',
    openLink: 'Open Link',
    backToTop: 'Back to top',
    badgeProgress: 'Badge Progress',
    badgeProgressCount: '{{completed}} of {{total}}',
    playVideo: 'Play video',
    trailer: 'Trailer',
    unenrollTitle: 'Are you sure you want to leave this program?',
    unenrollConfirm: 'Leave',
    unenrollCancel: 'Stay',
    resetProgressTitle: 'Want to restart this program?',
    resetProgressOptionResetTitle: 'Yes, restart {{contentTitle}}',
    resetProgressOptionResetDescription:
      'All progress will be cleared, and you’ll start this program from Day 1.',
    resetProgressOptionKeepTitle: 'No, keep my progress',
    resetProgressOptionKeepDescription:
      'The video watch history won’t reset. You’ll keep making progress toward your badge.',
    resetProgressProceed: 'Proceed',
    enrollLimitTitle: 'Commitment takes focus',
    enrollLimitDescription:
      'You’re already enrolled in 3 programs. Leave one of your current programs to commit to this one.',
    enrollLimitConfirm: 'Got it',
    trackSelectionTitle: 'Choose your track',
    trackSelectionDescription:
      'Pick the track that fits your goals. You can switch tracks anytime by leaving this one first.',
    trackSelectionConfirm: 'Commit',
    trackSelectionCalendarLabel: 'View calendar',
    enrollmentConfirmationTitle: 'You’re committed!',
    enrollmentConfirmationDescription:
      '{{trackTitle}} is ready for you. Your schedule is waiting in Workouts.',
    enrollmentConfirmationCta: 'Let’s go',
    restDay: 'Rest Day',
    optionalWorkout: 'Optional',
    dayLabel: 'Day {{number}}',
  },
  'geo-redirection-modal': {
    ...EN_US['geo-redirection-modal'],
  },
  programsPage: merge(cloneDeep(EN_US.programsPage)),
  workoutsPage: merge(cloneDeep(EN_US.workoutsPage)),
  allWorkoutsPage: merge(cloneDeep(EN_US.allWorkoutsPage)),
  allFitnessProgramsPage: merge(cloneDeep(EN_US.allFitnessProgramsPage)),
  badgesPage: merge(cloneDeep(EN_US.badgesPage)),
  fitnessQuizPage: merge(cloneDeep(EN_US.fitnessQuizPage)),
  programQuiz: EN_US.programQuiz,
  favoritesPage: EN_US.favoritesPage,
  homePage: EN_US.homePage,
  postLogin: EN_US.postLogin,
  accountPage: merge(cloneDeep(EN_US.accountPage), {
    emailChangeFaqUrl: 'https://faq.bodi.com/ca/en/contactus-support?category_id=1757',
  }),
  accountPayments: merge(cloneDeep(EN_US.accountPayments)),
  'cart-page': {
    'your-cart': 'Your cart',
    'cart-empty': 'Your cart is currently empty',
    'cart-empty-cta': 'Continue Shopping',
    'continue-to-checkout': 'Continue to Checkout',
    'order-summary': 'Order Summary',
    subtotal: 'Subtotal',
    'subtotal-disclaimer': '*Shipping & taxes calculated at checkout',
  },
  'forgot-password': merge(cloneDeep(EN_US['forgot-password']), {
    'help-center':
      'Didn\u2019t get an email? Please check your <a href="https://faq.bodi.com/ca/en/answer?Id=1273" target="_blank">help center</a>.',
  }),
  'create-password': merge(cloneDeep(EN_US['create-password'])),
  'update-password': merge(cloneDeep(EN_US['update-password'])),
  'outage-splash': {
    ...EN_US['outage-splash'],
  },
  countries: EN_US.countries,
  languages: EN_US.languages,
  'media-panel': {
    'go-to-program': 'Go to Program',
    'start-workout': 'Start Workout',
    'watch-video': 'Watch Video',
    'coming-soon': 'COMING SOON',
    'associated-content': 'Related Content',
    'works-well-for': 'Works well for',
    'equipment-needed': 'Equipment Needed',
    optional: '* Optional',
    'container-equivalents': 'Container Equivalents',
    'vegan-container-equivalents': 'Vegan Container equivalents',
    'view-pdf': 'View PDF',
    'copy-link': 'Copy link',
    'copy-link-success': 'Link Copied!',
    favorite: 'Favorite',
    favorited: 'Favorited',
    share: 'Share',
  },
  'content-share-modal': {
    'join-me': 'Join me for {{title}}, a 3-week fitness program: ',
    share: 'Share',
    email: 'Email',
    'copy-link': 'Copy Link',
    'copy-link-success': 'Copied!',
  },
  faq: {
    ...EN_US.faq,
    'sms-phone-invalid': 'Please enter a valid Canadian phone number',
  },
  badges: EN_US.badges,
  'badge-detail': EN_US['badge-detail'],
  'primary-badge-modal': EN_US['primary-badge-modal'],
  'set-password-form': EN_US['set-password-form'],
  search: EN_US.search,
  'video-waiver': EN_US['video-waiver'],
}

export const FR_CA: Dictionary = {
  postWorkout: {
    thatsHowItsDone: 'C’est comme ça qu’on réussit!',
    greatJobToday: 'Bon travail aujourd\u2019hui, {{name}} !',
    doneForToday: 'Fait pour aujourd’hui',
  },
  'set-password-form': {
    title: 'Accéder à ton profil dans l’Application BODi',
    cta: 'DÉFINIR LE MOT DE PASSE',
    ruleMinEightChars: 'Au moins 8 caractères',
    ruleOneUpper: 'Lettres majuscules (A-Z)',
    ruleOneLower: 'Lettres minuscules (a-z)',
    ruleOneNumber: 'Chiffres (0-9)',
    checklist: JSON.stringify([
      'Accéder au contenu de l’Application BODi',
      'Gérer les détails de ton compte',
      'Afficher les commandes antérieures',
      'Gérer les abonnements et les souscriptions',
    ]),
    'confirmation-title': 'Le tour est joué! Obtenir l’application.',
    'confirmation-title-ios': 'Commencez votre parcours de mise en forme',
    'confirmation-subtitle':
      'Profite de BODi sur ton téléphone, ta tablette, ton vélo ou ton ordinateur!',
    'confirmation-subtitle-ios':
      'Accédez à BODi sur votre téléphone, votre tablette ou votre ordinateur',
    'confirmation-cta': 'Démarrer',
  },
  'login-form': {
    title: 'Se connecter',
    email: 'Courriel',
    password: 'Mot de passe',
    cta: 'Se connecter',
    'email-required': 'L’adresse courriel est requise',
    'email-invalid': 'Veuillez saisir une adresse courriel valide',
    'password-required': 'Le mot de passe est requis',
    'forgot-password': 'Mot de passe oublié',
    'invalid-credentials': `L'adresse courriel ou le mot de passe que vous avez saisi est incorrect. Veuillez réessayer.`,
    'unknown-error': 'Oups… une erreur est survenue',
  },
  'checkout-form-success': {
    'partner.success': 'Cela confirme que {{partner}} est mon partenaire BODi.',
    'partner.disclaimer':
      'Only your contact and product information will be made available to your free BODi Partner who may reach out to help support you with your health and wellness goals. You can stop sharing your contact information at any time. FR',
  },
  'order-confirmation': {
    yourOrderIsConfirmed: 'Ta commande est confirmée',
    orderNumber: 'N° de commande: {{orderNumber}}',
    youWillGetConfirmationAt:
      'Tu recevras bientôt une confirmation de commande à l’adresse suivante : {{customerEmail}}',
    printReceipt: 'Imprimer le reçu',
    yourOrder: 'Ta commande',
    subtotal: 'Sous-total',
    estimatedTax: 'Estimation des taxes',
    total: 'Total',
    billingAndPayment: 'Facturation et paiement',
    cardDetails: `{{cardBrand}} se termine par {{cardNumber}}, expires le {{cardExpiration}}`,
    'total-savings': 'Total des économies',
    shipping: 'Frais de livraison et de manutention',
    'error-unknown-title': 'Oups… essaie encore une fois',
    'error-unknown-message':
      'Quelque chose a mal tourné dans les coulisses. Essaie encore une fois.',
    'error-unknown-cta': 'Réessayer',
  },
  test: {
    title: 'Accueil du Commerce Web',
  },
  common: {
    currency: '${{amount}}',
    saving: 'Économisez',
    suggestions: 'Suggestions',
    and: 'et',
    'see-all': 'Afficher tout',
    'bodi.com': 'bodi.com',
    'date-pattern': 'MM/DD/YYYY',
    'partner-placeholder': 'Identifiant ou courriel du partenaire (facultatif)',
    quantity: 'Qty: {{quantity}}',
    'error-unknown-title': 'Oups… une erreur est survenu',
    'error-unknown-message': 'Nous n’avons pas pu ajouter ceci à ton panier.',
    'error-unknown-cta': "D'accord",
    'error-retry-title': 'Oups… essaie encore une fois',
    'error-retry-message': 'Quelque chose a mal tourné dans les coulisses. Essaie encore une fois.',
    'error-retry-cta': 'Réessayer',
    'identity-outage-title': 'Création d’un compte impossible',
    'identity-outage-message': 'Nous n’avons pas pu créer ton compte. Tu peux ressayer plus tard.',
    'identity-outage-cta': 'Page d’accueil',
    'page-title-suffix': '| BODi de Beachbody',
    'app-title-suffix': '| BODi',
    'view-cart': 'Voir le panier',
    description: 'Description',
    share: 'Partager',
    'how-to-use': 'Directives d’utilisation',
    'key-ingredients': 'Ingrédients clés',
    'error-quantity':
      'Tu ne peux pas ajouter plus de {{quantity}} exemplaires de cet article à ton panier.',
    free: 'Gratuit',
    select: 'Sélectionner',
    login: 'Se connecter',
    'login-join': 'Se connecter',
    'my-account': 'Mon compte',
    'my-dashboard': 'Mon tableau de bord',
    'digital-purchases': 'Achats numériques',
    favorites: 'Favoris',
    badges: 'Badges',
    'no-badges-available': 'Aucun badge disponible.',
    tracking: 'Suivi',
    'my-progress': 'Mes progrès',
    with: 'avec',
    'try-free': 'Essayez GRATUITEMENT',
    upNext: 'A suivre',
    'view-program': 'Voir le programme',
    'retake-quiz': 'Refaire le quiz',
  },
  carousel: {
    'next-slide': 'Diapositive Suivante',
    'previous-slide': 'Diapositive Précédente',
    'slide-number': 'Diapositive {{number}}',
  },
  'content-details': {
    optional: '* Facultatif',
    programOverview: 'Aperçu du programme',
    overview: 'Aperçu',
    equipmentNeeded: 'Équipement requis',
    programFocus: 'Objectif du programme',
    meet: 'DÉCOUVREZ',
    startWorkout: 'Démarrer la séance',
    watchVideo: 'Regarder la vidéo',
    commit: 'S’engager',
    committed: 'En cours',
    startProgram: 'Commencer le programme',
    leave: 'Quitter',
    reset: 'Réinitialiser',
    enroll: 'S’inscrire',
    enrolled: 'Inscrit',
    favorite: 'Préféré',
    favorited: 'Favori',
    before: 'Avant',
    after: 'Après',
    openPdf: 'Ouvrir le PDF',
    openLink: 'Ouvrir le lien',
    backToTop: 'Retour en haut',
    badgeProgress: 'Progression du badge',
    badgeProgressCount: '{{completed}} sur {{total}}',
    playVideo: 'Lire la vidéo',
    trailer: 'Bande-annonce',
    unenrollTitle: 'Êtes-vous sûr de vouloir quitter ce programme ?',
    unenrollConfirm: 'Quitter',
    unenrollCancel: 'Rester',
    resetProgressTitle: 'Voulez-vous redémarrer ce programme ?',
    resetProgressOptionResetTitle: 'Oui, redémarrer {{contentTitle}}',
    resetProgressOptionResetDescription:
      'Tout progrès sera effacé, et vous recommencerez ce programme dès le Jour 1.',
    resetProgressOptionKeepTitle: 'Non, garder mes progrès',
    resetProgressOptionKeepDescription:
      'L’historique des vidéos visionnées ne sera pas réinitialisé. Vous continuerez à progresser vers votre badge.',
    resetProgressProceed: 'Continuer',
    enrollLimitTitle: 'L’engagement demande de la concentration',
    enrollLimitDescription:
      'Vous êtes déjà inscrit·e à 3 programmes. Quittez l’un d’eux pour vous engager dans celui-ci.',
    enrollLimitConfirm: 'Compris',
    trackSelectionTitle: 'Choisissez votre parcours',
    trackSelectionDescription:
      'Choisissez le parcours qui correspond à vos objectifs. Vous pouvez changer de parcours à tout moment en quittant d’abord celui-ci.',
    trackSelectionConfirm: 'S’engager',
    trackSelectionCalendarLabel: 'Voir le calendrier',
    enrollmentConfirmationTitle: 'Vous êtes engagé·e !',
    enrollmentConfirmationDescription:
      '{{trackTitle}} vous attend. Votre programme se trouve dans Entraînements.',
    enrollmentConfirmationCta: 'C’est parti',
    restDay: 'Jour de repos',
    optionalWorkout: 'Optionnel',
    dayLabel: 'Jour {{number}}',
  },
  'product-page': {
    shakeology: 'Shakeology',
    'facet-title-flavor': 'Saveur',
    'facet-title-packaging': 'Emballage',
    'facet-title-subscriptionFrequency': 'Fréquence',
    'facet-title-keyIngredient': 'Ingrédient clé',
    'facet-title-size': 'Taille',
    quantity: 'Quantité',
    'add-to-cart': 'Ajouter au panier',
    'subscription-discount-disclaimer':
      '^Le prix réduit s’applique uniquement au premier achat, les renouvellements suivants sont de {{amount}}.',
    'pinch-to-zoom': 'Zoomer',
  },
  'geo-redirection-modal': {
    title: 'Changer le pays de livraison?',
    subtitle:
      'Il semblerait que tu sois dans une autre région. Veux-tu modifier ton pays de livraison?',
    'select-your-country': 'Sélectionne ton pays',
    confirm: 'Confirmer',
    cancel: 'Non merci',
  },
  programsPage: {
    title: 'Programmes',
    metaTitle: 'Programmes d’entraînement BODi',
    description:
      'Trouve le programme d’entraînement BODi qui te convient. Avec plus de 140 programmes à découvrir, tous conçus pour donner des résultats en quelques semaines, tu trouveras des entraînements qui te plairont vraiment.',
    socialFeedTitle: 'Suis le parcours',
    socialFeedSubtitle: 'Découvre ce que partage la communauté',
    learnMore: 'En savoir plus',
    goToProgram: 'Voir le programme',
  },
  workoutsPage: {
    title: 'Séances d’entraînement',
    metaTitle: 'Séances d’entraînement BODi',
    description:
      'Trouve des séances d’entraînement que tu vas adorer. De la musculation au yoga, en passant par le cardio et la barre, BODi propose des séances d’entraînement pour tous les niveaux et de différentes durées.',
  },
  allWorkoutsPage: {
    title: 'Entraînements',
    metaTitle: "Parcourir toutes les séances d'entraînement BODi",
    description:
      "Choisis parmi toute une sélection de séances d'entraînement BODi dirigées par des experts. Filtre tes recherches en fonction de la durée, du niveau de difficulté, de l'équipement requis et plus encore.",
    viewResults: 'Voir entraînements',
    loadingMore: 'Chargement…',
  },
  allFitnessProgramsPage: {
    title: 'Programmes',
    metaTitle: 'Parcourir tous les programmes de fitness BODi',
    description:
      'Trouve le programme d’entraînement BODi qui te convient. Filtre par coach, durée, niveau de difficulté, équipement et plus encore.',
    viewResults: 'Voir programmes',
    loadingMore: 'Chargement…',
  },
  badgesPage: {
    title: 'Badges',
    metaTitle: 'BODi Badges',
    description: 'View your earned badges and track your progress.',
  },
  fitnessQuizPage: {
    metaTitle: 'Débloquez des séances d’entraînement BODi gratuites grâce à notre quiz fitness',
    titleSuffix: 'BODi',
    description:
      'Répondez à un quiz fitness d’une minute pour débloquer vos séances d’entraînement gratuites et personnalisées de BODi (anciennement Beachbody).',
    keywords: 'Quiz fitness',
  },
  programQuiz: {
    back: 'Retour',
    skip: 'Passer',
    skipAlertTitle: 'Ne manquez pas ça!',
    skipAlertMessage: 'Répondez à au moins une question pour obtenir des résultats personnalisés',
    skipAlertCta: 'Compris',
    continue: 'Continuer',
    processingTitle: 'Un instant!',
    processingMessage: 'Nous cherchons vos correspondances…',
    errorTitle: 'Impossible de charger vos résultats',
    errorMessage: 'Fermez le questionnaire et réessayez.',
    close: 'Fermer',
    greeting: 'Bonjour {{firstName}},',
    greetingMessage: 'voici vos résultats…',
    topRecommendation: 'Votre meilleure recommandation',
    otherRecommendations: 'Vos autres recommandations',
    getStarted: 'Pour commencer',
    viewProgram: 'Voir le programme',
    findYourPerfectProgram: 'Trouvez votre programme idéal',
    takeTheQuiz: 'Faire le quiz',
  },
  favoritesPage: {
    workouts: 'Entraînements',
    programs: 'Programmes',
    blocks: 'Blocs',
    recipes: 'Recettes',
    lessons: 'Leçons',
    more: 'Plus',
    recentlySaved: 'Récemment enregistrés',
    sortAZ: 'A–Z',
    emptyAllHeading: 'Favoris',
    emptyAllDescription: 'Sélectionne le cœur sur tout ce que tu aimes pour l’enregistrer ici.',
    browsePrograms: 'Parcourir les programmes',
    emptyTab: 'Aucun favori dans cette catégorie.',
  },
  homePage: {
    welcome: 'Bon retour!',
    welcomeWithName: 'Bienvenue, {{firstName}}!',
    goTo: 'Aller a {{title}}',
    myProgress: 'Mes progrès',
    limitedEditionBadges: 'Badges en édition limitée actuels',
    workoutsCompleted: 'Entraînements terminés',
    thisWeek: 'Cette semaine',
    lastWeek: 'La semaine dernière',
    recentlyEarned: 'Gagnés récemment',
    badgeProgress: 'Progrès du badge',
    extraSubtitle: 'Commençons!',
    whatsNewBlog: 'Quoi de neuf sur le blogue',
  },
  postLogin: {
    successHeadline: 'Connexion réussie.',
    continue: 'Continuer',
    dontShowAgain: 'Ne plus afficher ce message',
  },
  accountPage: {
    account: 'Compte',
    accountSubheader: 'Gérer et mettre à jour les renseignements de ton compte BODi',
    accountSettings: 'Paramètres du compte',
    shippingAddresses: 'Adresses de livraison',
    orders: 'Mes commandes',
    subscriptions: 'Abonnements & Adhésions',
    name: 'Nom',
    email: 'Courriel',
    emailChangeHint: 'Pour modifier ton adresse courriel, contacte nous',
    emailChangeFaqUrl: 'https://faq.bodi.com/ca/fr/contactus-support?category_id=1757',
    password: 'Mot de passe',
    language: 'Langue',
    pricacyAndPreferences: 'Confidentialité et préférences',
    updateYourPrivacyPreferences:
      'Mettre à jour tes préférences en matière de confidentialité et de courrier électronique',
    edit: 'Modifier',
    editName: 'Modifier le nom',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    editPassword: 'Modifier le mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le nouveau mot de passe',
    editLanguage: 'Modifier la langue',
    update: 'Mettre à jour',
    cancel: 'Annuler',
    nameUpdated: 'Ton nom a été mis à jour',
    passwordUpdated: 'Ton mot de passe a été mis à jour',
    languageUpdated: 'Ta langue a été mise à jour',
    unsupported: '(incompatible)',
    error: 'Impossible d’enregistrer les modifications, tu peux réessayer plus tard.',
  },
  accountPayments: {
    noPaymentMethods:
      'Tu n’as actuellement aucun moyen de paiement enregistré. Clique sur le bouton « Ajouter » pour enregistrer un moyen de paiement.',
    primary: 'Principal',
    expired: 'Expiré',
    add: 'Ajouter',
    edit: 'Modifier',
    update: 'Mettre à jour',
    delete: 'Supprimer la carte',
    deleteModalTitle: 'Supprimer le moyen de paiement?',
    deleteModalDescription: 'Tu es sûr de vouloir supprimer ce moyen de paiement?',
    deleteModalCancel: 'Annuler',
    deleteModalDelete: 'Supprimer',
    addPaymentMethod: 'Ajouter un moyen de paiement',
    editPaymentMethod: 'Modifier le moyen de paiement',
    billTo: 'Facturer à ',
    sameAsShippingAddress: 'Même adresse que l’adresse de livraison principale',
    expires: 'Expire le',
    expiresShort: 'Exp.',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    country: 'Pays',
    address: 'Adresse',
    addressNumber: 'Appartement, bureau, etc. (facultatif)',
    city: 'Ville',
    state: JSON.stringify({
      US: 'État',
      CA: 'Province',
    }),
    zip: JSON.stringify({
      US: 'Code postal',
      CA: 'Code postal',
    }),
    setAsPrimaryPayment: 'Définir comme moyen de paiement principal',
    save: 'Enregistrer',
    cancel: 'Annuler',
    paymentMethodCreated: 'Moyen de paiement créé',
    paymentMethodUpdated: 'Moyen de paiement mis à jour',
    paymentMethodDeleted: 'Moyen de paiement supprimé',
    error: 'Impossible d’enregistrer les modifications, tu peux réessayer plus tard.',
    updateWarning:
      '<b>Important:</b> Les modifications apportées au moyen de paiement ne mettront pas à jour les commandes existantes que vous avez passées. Pour mettre à jour les abonnements, veuillez accéder à l’onglet <a class="underline" href="{{subscriptionsAndMembershipsUrl}}">Abonnements & Adhésions</a>.',
  },
  'cart-page': {
    'your-cart': 'Ton panier',
    'cart-empty': 'Ton panier est actuellement vide',
    'cart-empty-cta': 'Poursuivre le magasinage',
    'continue-to-checkout': 'Passer à la caisse',
    'order-summary': 'Sommaire de la commande',
    subtotal: 'Sous-total',
    'subtotal-disclaimer': '*Livraison et taxes calculés à la caisse',
  },
  'forgot-password': {
    email: 'Adresse courriel',
    'reset-password': 'Réinitialiser ton mot de passe',
    'enter-email':
      'Saisissez l\u2019adresse électronique que vous avez utilisée pour vous inscrire et nous vous enverrons un courriel de récupération du mot de passe.',
    'send-email': 'Envoyer le courriel',
    'email-sent': 'E-mail envoyé',
    'send-instructions':
      'Si un compte avec {{email}} existe, nous enverrons les instructions de réinitialisation à cette adresse.',
    'help-center':
      'Tu n\u2019as pas reçu d\u2019e-mail? Consulte notre <a href="https://faq.bodi.com/ca/fr/answer?Id=11786" target="_blank">centre d\u2019assistance</a>.',
  },
  'create-password': {
    title: 'Créez votre mot de passe',
    description:
      'Entrez l\u2019adresse courriel que vous avez utilisée pour votre commande. Nous vous enverrons un courriel intitulé «\u00a0Votre réinitialisation du mot de passe BODi\u00a0». Comme c\u2019est votre première connexion, utilisez le lien dans ce courriel pour créer votre mot de passe.',
  },
  'update-password': {
    'page-title': 'Mettre à jour le mot de passe',
    'page-heading': 'Modifier votre mot de passe',
    'page-description':
      'Votre mot de passe aide à protéger votre compte. Choisissez-en un nouveau, fort et unique, en respectant les exigences ci-dessous.',
    submit: 'Mettre à jour le mot de passe',
    cancel: 'Annuler',
    'password-updated': 'Votre mot de passe a été mis à jour',
    'success-message': 'Votre mot de passe a été mis à jour avec succès.',
    'continue-to-account': 'Continuer vers le compte',
  },
  'outage-splash': {
    heading: 'Système en maintenance',
    description:
      'Veuillez nous excuser, mais cette page est temporairement hors ligne. Veuillez réessayer plus tard.',
  },
  countries: {
    US: 'États-Unis',
    CA: 'Canada',
    GB: 'Royaume-Uni',
    FR: 'France',
  } satisfies Record<CountryCodeTBB, string>,
  languages: {
    en: 'Anglais',
    es: 'Espagnol',
    fr: 'Français',
  } satisfies Record<LangTBB, string>,
  'media-panel': {
    'go-to-program': 'Aller au programme',
    'start-workout': 'Commencer l’Entraînement',
    'watch-video': 'Regarder la Vidéo',
    'coming-soon': 'À VENIR',
    'associated-content': 'Contenu associé',
    'works-well-for': 'Idéal pour',
    'equipment-needed': 'Équipement requis',
    optional: '* Facultatif',
    'container-equivalents': 'Équivalence en récipients',
    'vegan-container-equivalents': 'Équivalence en récipients véganes',
    'view-pdf': 'Voir le PDF',
    'copy-link': 'Copier le lien',
    'copy-link-success': 'Lien copié',
    favorite: 'Préféré',
    favorited: 'Favori',
    share: 'Partager',
  },
  'content-share-modal': {
    'join-me': 'Rejoignez-moi pour {{title}}, un programme de fitness de 3 semaines: ',
    share: 'Partager',
    email: 'Courriel',
    'copy-link': 'Copier le Lien',
    'copy-link-success': 'Copié!',
  },
  faq: {
    'page-title': 'BODi Support',
    'page-description':
      'Contactez le support client et lisez les questions fréquemment posées sur les abonnements BODi, Shakeology et plus encore.',
    'hero-title': 'Comment pouvons-nous vous aider?',
    'hero-subtitle': 'Recherchez des réponses ou parcourez les sujets ci-dessous',
    'search-placeholder': "Rechercher de l'aide...",
    'self-service-title': 'Outils en libre-service',
    'browse-categories-title': 'Parcourir les FAQ par sujet',
    'top-answers-title': 'Meilleures réponses',
    'see-more': 'Voir plus',
    'see-less': 'Voir moins',
    'see-more-results': 'Voir toutes les réponses',
    'all-answers-title': 'Toutes les réponses',
    'all-answers-description':
      'Parcourez toutes les réponses disponibles de notre base de connaissances.',
    'search-results-title': 'Résultats de recherche',
    'search-results-for': 'Résultats pour',
    'search-results-count': '{count} résultats trouvés',
    'search-no-results': 'Aucun résultat trouvé',
    'search-no-results-message':
      "Nous n'avons trouvé aucune réponse correspondant à votre recherche. Essayez avec des mots-clés différents ou parcourez nos sujets ci-dessous.",
    'search-try-again': 'Essayez une nouvelle recherche',
    'search-back-to-home': "Retour à l'accueil FAQ",
    'not-found-title': 'Oops...',
    'not-found-message':
      "Désolé, nous n'avons pas pu trouver ce que vous cherches. La page a peut-être été déplacée ou supprimée.",
    'sms-title': 'Texter avec BODi',
    'sms-text-with-bodi': 'Texter avec BODi',
    'sms-text-with-bodi-description':
      'Démarrez une conversation avec notre équipe de soutien par message texte',
    'sms-login-to-text': 'Se connecter pour texter avec BODi',
    'sms-login-description':
      'Connectez-vous pour démarrer une conversation avec notre équipe de soutien par message texte',
    'sms-mobile-number': 'Numéro de téléphone',
    'sms-text-us': 'Nous envoyer un texto',
    'sms-hours-of-operation': "Heures d'ouverture",
    'sms-sent-successfully': 'SMS envoyé avec succès!',
    'sms-failed': "Échec de l'envoi du SMS. Veuillez réessayer.",
    'sms-phone-required': 'Le numéro de téléphone est requis',
    'sms-phone-invalid': 'Veuillez entrer un numéro de téléphone valide du Canada',
    'sms-error-occurred': "Une erreur s'est produite. Veuillez réessayer.",
    'sms-greeting': 'Salut {{userName}}, vérifie ou modifie simplement ton numéro pour commencer.',
    'sms-phone-placeholder': '+1 (555) 555-5555',
    'sms-description':
      "Cliquez sur Nous envoyer un texto pour envoyer et recevoir des messages de support client avec nous. Des frais d'utilisation et de données peuvent s'appliquer.",
    'sms-current-time-label': 'Il est actuellement {{currentTime}}',
    'sms-outside-hours':
      "Le service SMS est actuellement indisponible en dehors des heures d'ouverture.",
    'sms-confirmation-title': 'Votre demande a été soumise',
    'sms-confirmation-subtitle':
      'Merci d’avoir démarré une conversation par texto. Veuillez vérifier votre appareil pour notre réponse.',
    email: 'Envoyer un e-mail à BODi',
    'email-description':
      'Envoyez-nous un email et notre équipe de support vous répondra dès que possible.',
    'busy-mode-close-button': 'Compris, merci.',
  },
  'user-registration-form-errors': {
    'firstName.required': 'Veuillez saisir un prénom.',
    'firstName.invalid': 'La longueur maximale autorisée est de 30 caractères.',
    'lastName.required': 'Veuillez saisir un nom de famille.',
    'lastName.invalid': 'La longueur maximale autorisée est de 30 caractères.',
    'email.exists':
      '<span>Vous possédez déjà ce produit numérique, <a href="/login-bodweb?locale=fr-CA">connectez-vous</a> pour consulter votre compte.',
    'email.invalid': 'Veuillez saisir une adresse courriel valide.',
    'password.invalid': 'Mot de passe non valide',
    'password.reused': 'Le nouveau mot de passe doit être différent du dernier mot de passe.',
    'password.mustMatch': 'Veuillez saisir de nouveau la même valeur.',
    'password.incorrect': 'Votre ancien mot de passe na pas été saisi correctement.',
    disclaimer:
      'En cochant cette case, vous reconnaissez avoir lu, compris et accepté les <link1>Conditions générales<link1> et la <link2>Politique de confidentialité<link2> de BODi.',
    tncErrorMessage: 'Veuillez accepter les conditions générales pour continuer.',
  },
  'user-registration-form': {
    email: 'Courriel',
    password: 'Mot de passe',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    login: 'Se connecter',
    haveAnAccount: 'Vous avez un compte ?',
    accountCreated: 'Compte créé avec succès!',
    congrats: 'Bravo!',
    accountCreated2: 'Votre compte a été créé.',
    continueToLogin: 'Continuer à vous connecter',
  },
  badges: {
    inProgress: 'En cours',
    earned: 'Obtenues',
    available: 'Disponibles',
    limitedEdition: 'Édition limitée',
  },
  'badge-detail': {
    share: 'Partager',
    'go-to-program': 'Aller au programme',
    'save-image': 'Enregistrer l’image',
    back: 'Retour',
    'remaining-workouts': 'Séances d’entraînement restantes',
    'next-badge': 'Prochain badge',
    'share-load-error': 'Impossible de charger le partage du badge. Veuillez réessayer.',
  },
  'primary-badge-modal': {
    title: 'Prêt pour la ronde {{iteration}} ?',
    subtitle: 'S’engager maintenant pour gagner de nouveau votre badge.',
    close: 'Fermer',
  },
  search: {
    title: 'Recherche',
    placeholder: 'Rechercher dans BODi',
    'recent-searches': 'Recherches récentes',
    'clear-all': 'Tout effacer',
    'no-results': 'Aucun résultat trouvé pour',
    'no-results-found': 'Aucun résultat trouvé',
    'close-label': 'Fermer la recherche',
    'results-for': 'Résultats de recherche pour « {{query}} »',
    'results-count': '{count} résultats',
    results: 'résultats',
    'items-count': '({{count}} articles)',
    filters: 'Filtres',
    'clear-filters': 'Effacer les filtres',
    'view-results': 'Voir les résultats',
    sortBy: 'Trier par :',
    viewResults: 'Voir les résultats',
    loadingMore: 'Chargement…',
    noResultsTitle: 'Vous nous avez trop étirés.',
    noResultsDescription:
      "Essayez d'assouplir votre recherche et commencez à voir les résultats instantanément.",
    errorTitle: 'On dirait qu’on s’est froissé un muscle',
    errorDescription:
      'Veuillez réessayer. Si le problème persiste nous le résoudrons dans les meilleurs délais.',
    retry: 'Réessayer',
    category: 'Catégorie',
    trainer: 'Entraîneur/Expert',
    workoutDuration: 'Durée',
    programDuration: 'Durée du programme',
    level: 'Niveau de fitness',
    fitnessLevelBeginner: 'Débutant',
    fitnessLevelIntermediate: 'Intermédiaire',
    fitnessLevelAdvanced: 'Avancé',
    fitnessLevelAllLevels: 'Tous niveaux',
    muscleGroup: 'Groupe musculaire',
    bodyPart: 'Partie du corps',
    equipment: 'Équipement',
    program: 'Programme',
    container: 'Récipients',
    dish: 'Plat',
    mainIngredient: 'Ingrédient principal',
    meal: 'Repas',
    worksWellFor: 'Convient aux',
    type: 'Type',
    workoutType: 'Types de séances d’entraînement',
    programType: 'Types de programme',
    noEquipment: 'Sans équipement',
    noEquipmentFacetValue: 'Sans équipement',
    studioWorkouts: 'Entraînements studio',
    studioWorkoutsFacetValue: 'Entraînements studio',
    sortRecommended: 'Popularité',
    sortNewest: 'Plus récent',
    sortTitleAsc: 'A-Z',
    numberWeeks: 'Nombre de semaines',
    'tab-all': 'Tous',
    'tab-programs': 'Programmes',
    'tab-workouts': "Séances d'entraînement",
    'tab-recipes': 'Recettes',
    'tab-lessons': 'Leçons',
    'tab-programMaterials': 'Ressources du programme',
    'tab-other': 'Autre',
  },
  'video-waiver': {
    body: VIDEO_WAIVER_BODY_FR,
    'checkbox-label':
      'En cliquant et cochant la case « Accepter », je reconnais avoir lu, compris et accepté le présent avertissement et la présente exonération.',
    accept: 'accepter',
    decline: 'refuser',
  },
}

export const EN_GB: Dictionary = merge(cloneDeep(EN_US), {
  common: {
    currency: '£{{amount}}',
    'date-pattern': 'DD/MM/YYYY',
  },
  'order-confirmation': {
    estimatedTax: 'Estimated VAT',
  },
  'forgot-password': {
    'help-center':
      'Didn\'t get an email? Please check your <a href="https://faq.bodi.com/gb/en/answer?Id=1273" target="_blank">help center</a>.',
  },
  'update-password': merge(cloneDeep(EN_US['update-password'])),
  faq: EN_US.faq,
} as Dictionary)

export const EN_AU: Dictionary = merge(cloneDeep(EN_US))

export const FR_FR: Dictionary = merge(cloneDeep(FR_CA), {
  badgesPage: {
    title: 'Badges',
    metaTitle: 'BODi Badges',
    description: 'View your earned badges and track your progress.',
  },
  workoutsPage: {
    description:
      'Découvre des séances d’entraînement que tu vas adorer. De l’haltérophilie au yoga, du cardio à la barre, BODi propose des séances d’entraînement pour tous les niveaux et de différentes durées.',
  },
  allWorkoutsPage: {
    description:
      'Choisis parmi une sélection de séances d’entraînement BODi dirigées par des experts en fitness. Recherche par durée, niveau de difficulté, équipement requis et plus encore.',
  },
  allFitnessProgramsPage: {
    description:
      'Trouve le programme d’entraînement BODi qui te convient. Filtre par coach, durée, niveau de difficulté, équipement et plus encore.',
  },
} as Dictionary)
